import { AUDIT_REF_NAME_MAX, type AuditFieldDef } from "./fields";
import {
  AUDIT_ACTION_DEFS,
  AUDIT_STRUCTS,
  type AuditAction,
  type AuditEntity,
  type AuditStructName
} from "./catalog";

/**
 * Funções puras sobre o catálogo — as mesmas no backend (gravar, filtrar) e no
 * frontend (mostrar). Sem I/O: validar um `data` custa microssegundos.
 */

export interface AuditDataProblem {
  field: string;
  /** missing: obrigatório ausente · type/enum: valor descartado · truncated: cortado no `max`. */
  reason: "missing" | "type" | "enum" | "truncated";
}

export interface BuiltAuditData {
  /** `<estrutura>@<versão>` — como ler o `data` deste registro para sempre. null = ação sem dados. */
  schema: string | null;
  data: Record<string, unknown> | null;
  problems: AuditDataProblem[];
}

const structOf = (action: AuditAction) => {
  const name = AUDIT_ACTION_DEFS[action]?.struct as AuditStructName | null | undefined;
  return name ? { name, def: AUDIT_STRUCTS[name] } : null;
};

const absent = (value: unknown): boolean => value === undefined || value === null || value === "";

const truncate = (text: string, max: number, field: string, problems: AuditDataProblem[]): string => {
  if (text.length <= max) return text;
  problems.push({ field, reason: "truncated" });
  return text.slice(0, max);
};

/** Converte um valor segundo o campo; `undefined` = descartado (problema já anotado). */
const coerce = (field: string, def: AuditFieldDef, value: unknown, problems: AuditDataProblem[]): unknown => {
  switch (def.kind) {
    case "text":
      if (typeof value === "string") return truncate(value, def.max, field, problems);
      if (typeof value === "number" || typeof value === "bigint") return truncate(String(value), def.max, field, problems);
      break;
    case "enum":
      if (typeof value === "string" && def.values.includes(value)) return value;
      problems.push({ field, reason: "enum" });
      return undefined;
    case "number":
      if (typeof value === "number" && Number.isFinite(value)) return value;
      break;
    case "ref": {
      const ref = value as { id?: unknown; name?: unknown } | null;
      const id = ref?.id;
      if (typeof id === "number" || (typeof id === "string" && id !== "")) {
        const out: Record<string, unknown> = { id };
        if (typeof ref?.name === "string" && ref.name !== "") {
          out.name = truncate(ref.name, AUDIT_REF_NAME_MAX, `${field}.name`, problems);
        }
        return out;
      }
      break;
    }
    case "secret":
      return undefined;
  }
  problems.push({ field, reason: "type" });
  return undefined;
};

/**
 * Monta o `meta.data` de uma ação a partir de qualquer objeto — inclusive a
 * instância do model. Lê SÓ os campos da estrutura: `secret` nunca, campo fora
 * do catálogo nunca, texto acima do `max` cortado. É o que mantém o registro
 * pequeno e sem dado que não devia estar ali.
 */
export const buildAuditData = (action: AuditAction, subject: object | null | undefined): BuiltAuditData => {
  const struct = structOf(action);
  if (!struct) return { schema: null, data: null, problems: [] };

  const source = (subject ?? {}) as Record<string, unknown>;
  const data: Record<string, unknown> = {};
  const problems: AuditDataProblem[] = [];

  for (const [field, def] of Object.entries(struct.def.fields) as [string, AuditFieldDef][]) {
    if (def.kind === "secret") continue;
    const value = source[field];
    if (absent(value)) {
      if (!def.optional) problems.push({ field, reason: "missing" });
      continue;
    }
    const coerced = coerce(field, def, value, problems);
    if (coerced !== undefined) data[field] = coerced;
  }

  return { schema: `${struct.name}@${struct.def.version}`, data, problems };
};

/** Campos que a API aceita como filtro `data.<campo>` para esta ação. */
export const auditFilterableFields = (action: AuditAction): string[] => {
  const struct = structOf(action);
  if (!struct) return [];
  return Object.entries(struct.def.fields as Record<string, AuditFieldDef>)
    .filter(([, def]) => def.kind !== "secret" && def.kind !== "ref" && def.filterable === true)
    .map(([field]) => field);
};

/** Entidade afetada por uma ação. */
export const auditEntityOf = (action: AuditAction): AuditEntity => AUDIT_ACTION_DEFS[action].entity;

/** Todas as ações de uma entidade — "histórico da conexão 12" filtra por elas. */
export const auditActionsOfEntity = (entity: string): AuditAction[] =>
  (Object.keys(AUDIT_ACTION_DEFS) as AuditAction[]).filter(action => AUDIT_ACTION_DEFS[action].entity === entity);

/** Entidades conhecidas pelo catálogo. */
export const auditEntities = (): string[] =>
  Array.from(new Set(Object.values(AUDIT_ACTION_DEFS).map(def => def.entity)));

/** Campo com um papel na tela (`title`/`subtitle`) na estrutura desta ação. */
export const auditFieldWithRole = (action: AuditAction, role: "title" | "subtitle"): string | null => {
  const struct = structOf(action);
  if (!struct) return null;
  const hit = Object.entries(struct.def.fields as Record<string, AuditFieldDef>).find(
    ([, def]) => def.kind !== "secret" && def.kind !== "ref" && def.role === role
  );
  return hit ? hit[0] : null;
};

/** Tipo do campo na estrutura da ação (null se não existir) — decide "contém" x igualdade no filtro. */
export const auditFieldKind = (action: AuditAction, field: string): AuditFieldDef["kind"] | null => {
  const struct = structOf(action);
  const def = struct ? (struct.def.fields as Record<string, AuditFieldDef>)[field] : undefined;
  return def ? def.kind : null;
};

/**
 * Campos `title` de todas as estruturas — é onde a busca livre procura o
 * alvo. Deriva do catálogo: estrutura nova com `title` entra sozinha.
 */
export const auditTitleFields = (): string[] =>
  Array.from(
    new Set(
      Object.values(AUDIT_STRUCTS).flatMap(struct =>
        Object.entries(struct.fields as Record<string, AuditFieldDef>)
          .filter(([, def]) => def.kind !== "secret" && def.kind !== "ref" && def.role === "title")
          .map(([field]) => field)
      )
    )
  );

/** Estrutura (nome) de uma ação — a tela usa para achar os rótulos dos campos. */
export const auditStructOf = (action: AuditAction): AuditStructName | null => structOf(action)?.name ?? null;
